import { collections, firestoreHelpers } from '@/lib/firebase';
import type { Message } from '@/types';

export const chatService = {
  // Send message
  async sendMessage(messageData: Partial<Message>): Promise<string> {
    const newMessage: Partial<Message> = {
      ...messageData,
      createdAt: new Date(),
      readAt: null,
    };
    
    return await firestoreHelpers.createDocument(collections.messages, newMessage);
  },

  // Send text message
  async sendTextMessage(
    rideId: string,
    senderId: string,
    senderType: 'passenger' | 'driver',
    content: string
  ): Promise<string> {
    return await this.sendMessage({
      rideId,
      senderId,
      senderType,
      type: 'text',
      content,
    });
  },

  // Send location message
  async sendLocationMessage(
    rideId: string,
    senderId: string,
    senderType: 'passenger' | 'driver',
    lat: number,
    lng: number
  ): Promise<string> {
    return await this.sendMessage({
      rideId,
      senderId,
      senderType,
      type: 'location',
      content: 'موقع مشترك',
      location: { lat, lng },
    });
  },

  // Send image message
  async sendImageMessage(
    rideId: string,
    senderId: string,
    senderType: 'passenger' | 'driver',
    imageURL: string
  ): Promise<string> {
    return await this.sendMessage({
      rideId,
      senderId,
      senderType,
      type: 'image',
      content: 'صورة',
      imageURL,
    });
  },

  // Send system message
  async sendSystemMessage(
    rideId: string,
    content: string
  ): Promise<string> {
    return await this.sendMessage({
      rideId,
      senderId: 'system',
      senderType: 'driver', // Using driver as system for type compatibility
      type: 'system',
      content,
    });
  },

  // Get message by ID
  async getMessage(messageId: string): Promise<Message | null> {
    return await firestoreHelpers.getDocument<Message>(collections.messages, messageId);
  },

  // Get ride messages
  async getRideMessages(rideId: string, limitCount?: number): Promise<Message[]> {
    return await firestoreHelpers.queryDocuments<Message>(
      collections.messages,
      [{ field: 'rideId', operator: '==', value: rideId }],
      'createdAt',
      'asc',
      limitCount
    );
  },

  // Mark message as read
  async markAsRead(messageId: string): Promise<void> {
    await firestoreHelpers.updateDocument(collections.messages, messageId, {
      readAt: new Date(),
    });
  },

  // Mark all messages as read
  async markAllAsRead(rideId: string, userId: string): Promise<void> {
    const messages = await this.getRideMessages(rideId);
    const unreadMessages = messages.filter(
      m => m.senderId !== userId && !m.readAt
    );
    
    for (const message of unreadMessages) {
      await this.markAsRead(message.id);
    }
  },

  // Get unread count
  async getUnreadCount(rideId: string, userId: string): Promise<number> {
    const messages = await this.getRideMessages(rideId);
    return messages.filter(
      m => m.senderId !== userId && !m.readAt
    ).length;
  },

  // Subscribe to ride messages
  subscribeToRideMessages(rideId: string, callback: (messages: Message[]) => void) {
    return firestoreHelpers.subscribeToQuery<Message>(
      collections.messages,
      [{ field: 'rideId', operator: '==', value: rideId }],
      callback
    );
  },

  // Delete message
  async deleteMessage(messageId: string): Promise<void> {
    await firestoreHelpers.deleteDocument(collections.messages, messageId);
  },

  // Quick messages
  quickMessages: {
    passenger: [
      'أنا في الموقع',
      'سأتأخر قليلاً',
      'أين أنت؟',
      'شكراً لك',
    ],
    driver: [
      'وصلت إلى الموقع',
      'أين أنت بالضبط؟',
      'سأكون هناك خلال 5 دقائق',
      'شكراً لاختيارك رحلة',
    ],
  },

  // Send quick message
  async sendQuickMessage(
    rideId: string,
    senderId: string,
    senderType: 'passenger' | 'driver',
    messageIndex: number
  ): Promise<string> {
    const messages = this.quickMessages[senderType];
    if (messageIndex >= 0 && messageIndex < messages.length) {
      return await this.sendTextMessage(
        rideId,
        senderId,
        senderType,
        messages[messageIndex]
      );
    }
    throw new Error('Invalid quick message index');
  }
};
